import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquarePlus, Loader2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FeedbackButton() {
  const { t, dir } = useI18n();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("suggestion");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (subject.trim().length < 2 || message.trim().length < 5) {
      toast.error(t("feedback_short") || "Please write a bit more so we can help.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/feedback`, {
        category,
        subject: subject.trim(),
        message: message.trim(),
        email: user ? undefined : (email || undefined),
        rating: rating || undefined,
      });
      toast.success(t("feedback_thanks") || "Thanks! We got your message.");
      setSubject(""); setMessage(""); setRating(0); setEmail("");
      setOpen(false);
    } catch (err) {
      toast.error(t("feedback_fail") || "Could not send. Try again.");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="feedback-fab"
          aria-label={t("feedback_open") || "Send feedback"}
          className="fixed bottom-5 end-5 z-40 h-12 w-12 rounded-full bg-emerald-500 text-black shadow-2xl shadow-emerald-900/40 hover:scale-110 transition flex items-center justify-center"
        >
          <MessageSquarePlus className="w-5 h-5" />
        </button>
      </DialogTrigger>
      <DialogContent dir={dir} className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle>{t("feedback_title") || "Support & suggestions"}</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {t("feedback_desc") || "Help us improve — report a bug, suggest a feature, or ask for support."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <label className="text-xs text-zinc-400">{t("feedback_type") || "Type"}</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="feedback-category" className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="suggestion">{t("feedback_cat_suggestion") || "Suggestion"}</SelectItem>
                <SelectItem value="bug">{t("feedback_cat_bug") || "Bug report"}</SelectItem>
                <SelectItem value="support">{t("feedback_cat_support") || "Support"}</SelectItem>
                <SelectItem value="other">{t("feedback_cat_other") || "Other"}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-zinc-400">{t("feedback_subject") || "Subject"}</label>
            <Input
              data-testid="feedback-subject"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={200}
              placeholder={t("feedback_subject_ph") || "Short title..."}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              required
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400">{t("feedback_message") || "Message"}</label>
            <Textarea
              data-testid="feedback-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              maxLength={4000}
              rows={5}
              placeholder={t("feedback_message_ph") || "Tell us more..."}
              className="bg-zinc-900 border-zinc-800 text-zinc-100"
              required
            />
          </div>
          {!user && (
            <div>
              <label className="text-xs text-zinc-400">{t("feedback_email") || "Your email (optional)"}</label>
              <Input
                data-testid="feedback-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="bg-zinc-900 border-zinc-800 text-zinc-100"
              />
            </div>
          )}
          <div>
            <label className="text-xs text-zinc-400">{t("feedback_rating") || "Rate the app"}</label>
            <div className="flex gap-1 mt-1">
              {[1,2,3,4,5].map(n => (
                <button
                  type="button"
                  key={n}
                  data-testid={`feedback-star-${n}`}
                  onClick={() => setRating(n === rating ? 0 : n)}
                  className="p-1"
                  aria-label={`${n} stars`}
                >
                  <Star className={"w-5 h-5 transition " + (n <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-600")} />
                </button>
              ))}
            </div>
          </div>
          <Button
            data-testid="feedback-submit"
            type="submit"
            disabled={busy}
            className="w-full h-10 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (t("feedback_send") || "Send")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
